import React, { useState } from 'react';
import { useData } from '../DataContext';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, User, Disc, Calendar, MessageSquare, LogOut, Menu, X, Settings, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { motion } from 'framer-motion';

const AdminLayout = () => {
  const { isAuthenticated, authLoading, logout } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  React.useEffect(() => {
    // Only redirect after auth check is complete
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Verifying access...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: User, label: 'About', path: '/admin/about' },
    { icon: Disc, label: 'Releases', path: '/admin/releases' },
    { icon: Calendar, label: 'Events', path: '/admin/events' },
    { icon: MessageSquare, label: 'Messages', path: '/admin/messages' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-background flex font-body">
      {/* Sidebar */}
      <motion.aside 
        initial={false}
        animate={{ width: isSidebarOpen ? 250 : 80 }}
        className="bg-card border-r border-white/10 flex flex-col fixed h-full z-20 transition-all duration-300"
      >
        <div className="p-6 flex items-center justify-between">
          {isSidebarOpen && (
            <span className="text-xl font-heading font-bold text-primary tracking-widest">ADMIN</span>
          )}
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!isSidebarOpen)} className="text-muted-foreground hover:text-primary">
            {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        <nav className="flex-1 px-4 space-y-2 mt-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive ? "secondary" : "ghost"}
                  className={`w-full justify-start gap-4 mb-2 ${isActive ? 'bg-primary/20 text-primary border border-primary/50' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {isSidebarOpen && <span>{item.label}</span>}
                </Button>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button 
            variant="destructive" 
            className="w-full justify-start gap-4"
            onClick={() => {
              logout();
              navigate('/');
            }}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {isSidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main 
        className={`flex-1 p-8 transition-all duration-300 ${isSidebarOpen ? 'ml-[250px]' : 'ml-[80px]'}`}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
